/**
 * loopbackServer — shared plumbing for the Tauri OAuth loopback flow.
 *
 * Both ``useDesktopOAuth`` (generic PKCE + code exchange) and
 * ``useDesktopIdpLogin`` (Zitadel IdP-intent flow) bind the same Rust
 * loopback listener (``start_local_oauth_server`` /
 * ``stop_local_oauth_server``) and consume the same
 * ``oauth-callback-loopback`` event. Everything they *share* lives
 * here — one canonical implementation, one place to fix bugs.
 *
 * Aug 26, 2026 consolidation:
 *   1. ``startLoopbackServer`` — thin typed wrapper around the Rust
 *      command. Returns the OS-assigned port so callers can compose the
 *      per-attempt ``redirect_uri``.
 *   2. ``stopLoopbackServer`` — idempotent teardown. Swallows errors
 *      because callers invoke this from ``finally`` blocks / unmount
 *      cleanups where a duplicate stop is expected (sibling hook drained
 *      the port first, component tore down mid-flow, etc.).
 *   3. ``PUBLIC_HEADERS`` — the empty-Bearer header the public auth
 *      endpoints need so the ``ApiClient`` interceptor doesn't inject a
 *      stale token from a prior session.
 *   4. ``coerceErrorMessage`` — the union of the three prior copies
 *      spread across ``useDesktopOAuth`` / ``useDesktopIdpLogin`` /
 *      ``DesktopLoginForm``. Handles strings, ``Error`` instances, and
 *      opaque JSON payloads (Tauri ``invoke`` rejections). Callers pass
 *      a Lingui-translated ``fallback`` so the surfaced message stays
 *      localised.
 *
 * NB: this module MUST NOT import from ``useDesktop*`` / ``desktopSession``
 * — those import from here and a cycle would break the vite build.
 */

import { invoke } from "@tauri-apps/api/core";

/**
 * Bind an ephemeral 127.0.0.1 listener via the Rust shell and return
 * the OS-assigned port. The caller composes the loopback ``redirect_uri``
 * as ``http://127.0.0.1:<port>/callback`` (RFC 8252).
 *
 * Throws on Tauri command failure — the login hook already surfaces the
 * message through its ``onError`` / state banner, so no defensive
 * swallow here.
 */
export async function startLoopbackServer(): Promise<number> {
  return await invoke<number>("start_local_oauth_server");
}

/**
 * Best-effort teardown of the loopback listener Rust bound for one
 * attempt. Idempotent — a callback that lands after a manual cancel
 * (or a duplicate ``oauth-callback-loopback`` fan-out to a sibling
 * hook) may find the listener already gone. Never throws so it's safe
 * to ``await`` inside ``finally``.
 */
export async function stopLoopbackServer(port: number): Promise<void> {
  try {
    await invoke("stop_local_oauth_server", { port });
  } catch {
    // Silent — the listener may already be down, or the current build
    // may not expose the command in a web smoke context.
  }
}

/**
 * ``Authorization: ""`` opts a request out of the ``ApiClient`` Bearer
 * interceptor — required on public auth endpoints (``/auth/desktop/...``
 * pre-login), otherwise the interceptor would attach a stale token from
 * a prior session and the BE would reject the call.
 *
 * Exposed as a constant so every call site reads the same shape and a
 * future rename lands in one place.
 */
export const PUBLIC_HEADERS = { Authorization: "" } as const;

/**
 * Coerce anything an auth path might throw into a readable message.
 *
 * Handles:
 *   - Plain strings (Tauri ``invoke`` rejects with the ``Err(String)``
 *     from the Rust command — NOT an ``Error`` instance).
 *   - ``Error`` instances (fetch failures, JSON parse errors).
 *   - Opaque JSON payloads (structured BE errors that survive as an
 *     unknown-shape object).
 *
 * The ``fallback`` MUST be a Lingui-translated string — the caller owns
 * the localisation because this helper is renderer-agnostic.
 */
export function coerceErrorMessage(err: unknown, fallback: string): string {
  if (typeof err === "string") return err || fallback;
  if (err instanceof Error) return err.message || fallback;
  try {
    return JSON.stringify(err) || fallback;
  } catch {
    return fallback;
  }
}
